import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, LayoutList, PanelLeft, SlidersHorizontal } from 'lucide-react';

import { IconButton, type IconButtonSize } from '@/components/IconButton';
import { type MenuMode, useMenuMode } from '@/hooks/useMenuMode';
import { cn } from '@/lib/utils';

/**
 * MenuModeSwitcher — jedna łatwo dostępna ikonka „ustawienia menu" (w nagłówku sidebara),
 * która otwiera małe menu z wyborem TRYBU rysowania menu tenanta:
 *  - 'list' — klasyczne menu (pełna szerokość, pozycje wybranego widoku).
 *  - 'tree' — wąski floating pasek ikon obszarów; treść wyjeżdża kaskadą na hover.
 * Prostopadłe do „Widoku menu" (zestaw pozycji) — patrz `useMenuMode`. Zamiast ciasnego
 * segmentowego przełącznika (decyzja Eugenii 2026-09-16): ikonka + rozwijane menu.
 */
// 'columns' (Mapa obszarów) NIE jest już osobnym trybem w przełączniku — mapa otwiera się
// po KLIKNIĘCIU W LOGO (decyzja Eugenii 2026-09-17). Zostają dwa tryby rysowania menu.
const OPTIONS: ReadonlyArray<{ mode: MenuMode; Icon: typeof LayoutList; labelKey: string }> = [
  { mode: 'list', Icon: LayoutList, labelKey: 'tenant.sidebar.menuMode.list' },
  { mode: 'tree', Icon: PanelLeft, labelKey: 'tenant.sidebar.menuMode.tree' },
];

export function MenuModeSwitcher({ size = 'sm' }: { size?: IconButtonSize }) {
  const { t } = useTranslation();
  const { mode, setMode } = useMenuMode();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      // Pozycja adaptacyjna — ten sam komponent żyje w nagłówku listy (góra-prawo) i w wąskim
      // pasku ikon (dół-lewo). Menu wyjeżdża OBOK ikonki (jak panele obszarów): w prawo gdy jest
      // miejsce (pasek), w lewo w nagłówku. Pionowo wyrównane, żeby nie uciekło z ekranu.
      const MENU_W = 208; // w-52
      const MENU_H = 120; // nagłówek + 2 pozycje (szacunek)
      const GAP = 8;
      const openRight = r.left < window.innerWidth / 2;
      const left = openRight ? r.right + GAP : r.left - MENU_W - GAP;
      // Wyrównanie pionowe: przy dole ekranu dół menu do dołu ikonki (rośnie w górę), inaczej góra do góry.
      const openUp = r.bottom > window.innerHeight / 2;
      const top = openUp ? r.bottom - MENU_H : r.top;
      setAnchor({ top, left });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <IconButton
        ref={triggerRef}
        size={size}
        variant="ghost"
        aria-label={t('tenant.sidebar.menuMode.label')}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t('tenant.sidebar.menuMode.label')}
        onClick={toggle}
        className={cn('text-text-muted', open && 'bg-bg-tertiary text-text-primary')}
        data-testid="menu-mode-switcher"
      >
        <SlidersHorizontal />
      </IconButton>

      {open &&
        anchor &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-testid="menu-mode-menu"
            className="fixed z-[10001] w-52 rounded-lg border border-border-secondary bg-bg-elevated p-1 shadow-lg"
            style={{ top: anchor.top, left: anchor.left }}
          >
            <div className="px-2.5 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              {t('tenant.sidebar.menuMode.label')}
            </div>
            {OPTIONS.map(({ mode: m, Icon, labelKey }) => {
              const active = mode === m;
              const choose = () => {
                setMode(m);
                setOpen(false);
              };
              return (
                <div
                  key={m}
                  role="menuitemradio"
                  aria-checked={active}
                  tabIndex={0}
                  onClick={choose}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      choose();
                    }
                  }}
                  className={cn(
                    'flex w-full cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                    active
                      ? 'bg-accent-50 text-accent-700'
                      : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary',
                  )}
                  data-testid={`menu-mode-${m}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{t(labelKey)}</span>
                  {active && <Check className="h-4 w-4 shrink-0" />}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
